import * as React from 'react';
import { AiSummaryModalDesktop } from '../../components/AiSummaryModalDesktop';
import type { IAiSummaryFooterActions } from '../../components/AiSummaryModalDesktop/types';
import type { IBuilder, ISeller } from '../../types/newbuildingsCompare';
import { NewbuildingCallButton } from '../NewbuildingCallButton';
import { NewbuildingChatButton } from '../NewbuildingChatButton';

const mockBuilderWithChat: IBuilder = {
  id: 1,
  realtyUserId: 1,
  name: 'Застройщик',
  logoUrl: '',
  chat: {
    enabled: true,
    suggestText: 'Здравствуйте!',
    context: {},
    entityId: '1',
  },
  phone: {
    countryCode: '7',
    code: '495',
    number: '1234567',
  },
};

const mockBuilderWithoutChat: IBuilder = {
  ...mockBuilderWithChat,
  id: 2,
  realtyUserId: 2,
  chat: null,
  phone: {
    countryCode: '7',
    code: '495',
    number: '1111111',
  },
};

const mockAgency: ISeller = {
  realtyUserId: 3,
  name: 'Агентство недвижимости',
  logoUrl: '',
  chat: null,
  phone: {
    countryCode: '7',
    code: '495',
    number: '7654321',
  },
};

interface IMockNewbuildingContacts {
  newbuildingId: number;
  url: string;
  builders: IBuilder[];
  sellers: ISeller[];
}

const mockNewbuildingContacts: IMockNewbuildingContacts[] = [
  {
    newbuildingId: 1,
    url: 'https://www.cian.ru/zhiloy-kompleks-badaevskiy-moskva-348843/',
    builders: [mockBuilderWithChat],
    sellers: [mockAgency],
  },
  {
    newbuildingId: 2,
    url: 'https://www.cian.ru/zhiloy-kompleks-simvol-moskva-348844/',
    builders: [mockBuilderWithoutChat],
    sellers: [],
  },
];

export interface IAiSummaryModalDesktopContainerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AiSummaryModalDesktopContainer = ({
  open,
  onOpenChange,
}: IAiSummaryModalDesktopContainerProps): React.JSX.Element => {
  const [buttonSlot, setButtonSlot] = React.useState<HTMLDivElement | null>(null);
  const [activeCallNewbuildingId, setActiveCallNewbuildingId] = React.useState<number | null>(null);

  const handleCallButtonSlotChange = React.useCallback((newbuildingId: number, slot: HTMLDivElement | null) => {
    setButtonSlot(slot);

    if (slot) {
      setActiveCallNewbuildingId(newbuildingId);
    }
  }, []);

  const activeNewbuildingContacts = mockNewbuildingContacts.find(
    newbuilding => newbuilding.newbuildingId === activeCallNewbuildingId,
  );

  const handleOpenContacts = React.useCallback(() => onOpenChange(false), [onOpenChange]);
  const handleCloseContacts = React.useCallback(() => onOpenChange(true), [onOpenChange]);

  const footerActions: IAiSummaryFooterActions[] = mockNewbuildingContacts.map(newbuilding => {
    const hasChat = [...newbuilding.builders, ...newbuilding.sellers].some(contact => contact.chat?.enabled);

    return {
      newbuildingId: newbuilding.newbuildingId,
      chat: hasChat ? (
        <NewbuildingChatButton
          builders={newbuilding.builders}
          newbuildingId={newbuilding.newbuildingId}
          sellers={newbuilding.sellers}
          size="M"
          url={newbuilding.url}
        />
      ) : undefined,
    };
  });

  return (
    <>
      <AiSummaryModalDesktop
        footerActions={footerActions}
        onCallButtonSlotChange={handleCallButtonSlotChange}
        onOpenChange={onOpenChange}
        open={open}
      />
      {activeNewbuildingContacts && (
        <NewbuildingCallButton
          key={activeNewbuildingContacts.newbuildingId}
          builders={activeNewbuildingContacts.builders}
          buttonSlot={buttonSlot}
          newbuildingId={activeNewbuildingContacts.newbuildingId}
          onCloseContacts={handleCloseContacts}
          onOpenContacts={handleOpenContacts}
          sellers={activeNewbuildingContacts.sellers}
          size="M"
          url={activeNewbuildingContacts.url}
        />
      )}
    </>
  );
};
